import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MainView } from '../../../templates/MainView';
import { Text } from '../../../components/atoms/Text';
import { Heading } from '../../../components/atoms/Heading';
import { Field } from '../../../components/molecules/Field';
import { Switch } from '../../../components/molecules/Switch';
import { ProgressBar } from '../../../components/molecules/ProgressBar';
import { useField, useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';
import { isAgeOrOver } from '../../../components/validation/age';
import requiredNumeric from '../../../components/validation/requiredNumeric';
import { useDropDown } from '../../../lib/useDropdown';

const hasValue = (v) => v !== undefined && v !== null && v !== '';

const kgToImperialWeight = (kg) => {
  const parsed = parseFloat(kg);
  if (!parsed) return null;

  const totalPounds = Math.round(parsed * 2.204623);
  return {
    st: Math.floor(totalPounds / 14),
    lbs: totalPounds % 14,
  };
};

const cmToImperialHeight = (cm) => {
  const parsed = parseFloat(cm);
  if (!parsed) return null;

  const totalInches = Math.round(parsed * 0.393700787);
  return {
    ft: Math.floor(totalInches / 12),
    in: totalInches % 12,
  };
};

const toKg = (st = 0, lbs = 0) => (st * 6.35 + lbs * 0.453).toFixed(2);

const toCm = (ft = 0, inch = 0) => (ft * 30.48 + inch * 2.54).toFixed(2);

export const BMIQuestions = ({ buttons = null, currentPage = 1, totalPages = 1 }) => {
  const { values } = useFormState();
  const assessment = values.assessment || {};

  const [weight, setWeight] = useState({ st: '', lbs: '' });
  const [height, setHeight] = useState({ ft: '', in: '' });

  const [weightSwitchIndex, setWeightSwitchIndex] = useState(assessment.weight_metric === 'kg' ? 0 : 1);
  const [heightSwitchIndex, setHeightSwitchIndex] = useState(1);
  
  const weightKgField = useField('assessment[assessment_weight_kg]');
  const weightStField = useField('assessment[assessment_weight_st]');
  const weightLbsField = useField('assessment[assessment_weight_lbs]');
  const weightMetricField = useField('assessment[weight_metric]');
  const heightCmField = useField('assessment[assessment_height_cm]');
  const heightFtField = useField('assessment[assessment_height_ft]');
  const heightInField = useField('assessment[assessment_height_in]');

  const preAssessment = useSelector(state => state.preassessment);

  const ethnicGroups = useDropDown('ethnicity', (values) => values.filter(obj => ['4 Not Disclosed'].indexOf(obj.label) === -1));

  useEffect(() => {
    const metricHeight = assessment.assessment_height_cm || preAssessment.height;
    const metricWeight = assessment.assessment_weight_kg || preAssessment.weight;
    const imperialHeight = hasValue(assessment.assessment_height_ft) || hasValue(assessment.assessment_height_in)
      ? {
          ft: assessment.assessment_height_ft ?? '',
          in: assessment.assessment_height_in ?? '',
        }
      : cmToImperialHeight(metricHeight);
    const imperialWeight = hasValue(assessment.assessment_weight_st) || hasValue(assessment.assessment_weight_lbs)
      ? {
          st: assessment.assessment_weight_st ?? '',
          lbs: assessment.assessment_weight_lbs ?? '',
        }
      : kgToImperialWeight(metricWeight);

    if (!assessment.weight_metric) {
      weightMetricField.input.onChange('st');
    }

    if (metricHeight && assessment.assessment_height_cm !== metricHeight) {
      heightCmField.input.onChange(metricHeight);
    }

    if (metricWeight && assessment.assessment_weight_kg !== metricWeight) {
      weightKgField.input.onChange(metricWeight);
    }

    if (imperialHeight) {
      setHeight((prev) => prev.ft === imperialHeight.ft && prev.in === imperialHeight.in ? prev : imperialHeight);

      if (!hasValue(assessment.assessment_height_ft)) {
        heightFtField.input.onChange(imperialHeight.ft);
      }

      if (!hasValue(assessment.assessment_height_in)) {
        heightInField.input.onChange(imperialHeight.in);
      }
    }

    if (imperialWeight) {
      setWeight((prev) => prev.st === imperialWeight.st && prev.lbs === imperialWeight.lbs ? prev : imperialWeight);

      if (!hasValue(assessment.assessment_weight_st)) {
        weightStField.input.onChange(imperialWeight.st);
      }

      if (!hasValue(assessment.assessment_weight_lbs)) {
        weightLbsField.input.onChange(imperialWeight.lbs);
      }
    }
  }, [
    assessment.assessment_height_cm,
    assessment.assessment_height_ft,
    assessment.assessment_height_in,
    assessment.assessment_weight_kg,
    assessment.assessment_weight_lbs,
    assessment.assessment_weight_st,
    assessment.weight_metric,
    preAssessment.height,
    preAssessment.weight,
  ]);

  useEffect(() => {
    setWeightSwitchIndex(assessment.weight_metric === 'kg' ? 0 : 1);
  }, [assessment.weight_metric]);

  const convertWeight = (kg) => {
    const imperial = kgToImperialWeight(kg);
    if (!imperial) return;

    setWeight(imperial);
    weightStField.input.onChange(imperial.st);
    weightLbsField.input.onChange(imperial.lbs);
  };

  const convertHeight = (cm) => {
    const imperial = cmToImperialHeight(cm);
    if (!imperial) return;

    setHeight(imperial);
    heightFtField.input.onChange(imperial.ft);
    heightInField.input.onChange(imperial.in);
  };

  const handleWeightChange = (e) => {
    const { name, value } = e.target;
    const weightFieldMap = {
      'assessment[assessment_weight_st]': 'st',
      'assessment[assessment_weight_lbs]': 'lbs',
    };
    const fieldKey = weightFieldMap[name];
    if (!fieldKey) return;

    const next = { ...weight, [fieldKey]: value };

    setWeight(next);
    weightStField.input.onChange(next.st);
    weightLbsField.input.onChange(next.lbs);
    weightKgField.input.onChange(toKg(next.st || 0, next.lbs || 0));
  };

  const handleHeightChange = (e) => {
    const { name, value } = e.target;
    const heightFieldMap = {
      'assessment[assessment_height_ft]': 'ft',
      'assessment[assessment_height_in]': 'in',
    };
    const fieldKey = heightFieldMap[name];
    if (!fieldKey) return;

    const next = { ...height, [fieldKey]: value };

    setHeight(next);
    heightFtField.input.onChange(next.ft);
    heightInField.input.onChange(next.in);
    heightCmField.input.onChange(toCm(next.ft || 0, next.in || 0));
  };

  const toggleWeightUnit = (idx) => {
    setWeightSwitchIndex(idx);
    weightMetricField.input.onChange(idx === 0 ? 'kg' : 'st');
  };

  const toggleHeightUnit = (idx) => {
    setHeightSwitchIndex(idx);
  };

  useEffect(() => {
    const handleWheel = () => {
      if (document.activeElement?.type === 'number') {
        document.activeElement.blur();
      }
    };

    document.addEventListener('wheel', handleWheel);
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <MainView BPadding2 allowOverflow>
      <ProgressBar min={0} max={totalPages} current={currentPage} label='Quick health check' />

      <div className='px-6 pt-8 pb-14 smd:p-12 flex flex-col'>
        <Text classes='mb-2' body>{`Step ${currentPage} of ${totalPages}`}</Text>
        <Heading classes='mb-2' h1>Let’s calculate your BMI!</Heading>
        <Text classes='mb-8' body>Calculating your Body Mass Index (BMI) can be helpful in identifying if you are within a healthy weight range.</Text>

        <div className='flex flex-col space-y-8 smd:space-y-14 pb-10 smd:pb-0'>
          <div className='flex flex-col items-end'>
            {weightSwitchIndex === 0 ? (
              <div className='relative w-full'>
                <Field
                  input='number'
                  name='assessment[assessment_weight_kg]'
                  label='Weight'
                  placeholder='Weight'
                  // modalsubtitle='Why do we ask this?'
                  // modaltitle='Weight'
                  // help='This is some help text'
                  isRequired
                  inputOnChange={(e) => convertWeight(e.target.value)}
                />
                <Heading classes='absolute right-4 top-lwd-53.5' h3>kg.</Heading>
              </div>
            ) : (
              <div className='flex flex-row gap-4 w-full items-start'>
                <div className='relative w-full'>
                  <Field
                    input='number'
                    name='assessment[assessment_weight_st]'
                    value={weight.st}
                    label='Weight'
                    placeholder='Stone'
                    isShowingRequired
                    validation={[(value) => requiredNumeric(hasValue(value) ? value : weight.st)]}
                    inputOnChange={handleWeightChange}
                  />
                  <Heading classes='absolute right-4 top-lwd-53.5' h3>st.</Heading>
                </div>

                <div className='relative w-full mt-lwd-35 smd:mt-lwd-39'>
                  <Field
                    input='number'
                    name='assessment[assessment_weight_lbs]'
                    value={weight.lbs}
                    placeholder='Pounds'
                    hideLabel
                    validation={[(value) => requiredNumeric(hasValue(value) ? value : weight.lbs)]}
                    inputOnChange={handleWeightChange}
                  />
                  <Heading classes='absolute right-4 top-lwd-14.5' h3>lbs.</Heading>
                </div>
              </div>
            )}

            <Switch classes='mt-2' labels={['kg.', 'st.']} selectedIndex={1} onChange={toggleWeightUnit} />
          </div>

          <div className='flex flex-col items-end'>
            {heightSwitchIndex === 0 ? (
              <div className='relative w-full'>
                <Field
                  input='number'
                  name='assessment[assessment_height_cm]'
                  label='Height'
                  placeholder='Height'
                  inputOnChange={(e) => convertHeight(e.target.value)}
                  isRequired
                />
                <Heading classes='absolute right-4 top-lwd-53.5' h3>cm.</Heading>
              </div>
            ) : (
              <div className='flex flex-row gap-4 w-full items-start'>
                <div className='relative w-full'>
                  <Field
                    input='number'
                    name='assessment[assessment_height_ft]'
                    label='Height'
                    placeholder='Foot'
                    value={height.ft}
                    isShowingRequired
                    validation={[(value) => requiredNumeric(hasValue(value) ? value : height.ft)]}
                    inputOnChange={handleHeightChange}
                  />
                  <Heading classes='absolute right-4 top-lwd-53.5' h3>ft.</Heading>
                </div>

                <div className='relative w-full mt-lwd-35 smd:mt-lwd-39'>
                  <Field
                    input='number'
                    name='assessment[assessment_height_in]'
                    value={height.in}
                    placeholder='Inch'
                    hideLabel
                    validation={[(value) => requiredNumeric(hasValue(value) ? value : height.in)]}
                    inputOnChange={handleHeightChange}
                  />
                  <Heading classes='absolute right-4 top-lwd-14.5' h3>in.</Heading>
                </div>
              </div>
            )}

            <Switch classes='mt-2' labels={['cm.', 'in.']} selectedIndex={1} onChange={toggleHeightUnit} />
          </div>

          {preAssessment.assessment_count === 0 && (<Field
            input='select'
            name='assessment[ethnicity]'
            label='Ethnicity'
            placeholder='Please select'
            isRequired
            inputOptions={ethnicGroups}
          />)}

          {preAssessment.gender !== 'Male' && !isAgeOrOver(preAssessment.dob, 65) && (
            <Field
              input='radio'
              name='assessment[assessment_pregnant]'
              label='Are you currently pregnant?'
              inputOptions={{
                options: [
                  {value: 'Yes', label: 'Yes', key: '1'},
                  {value: 'No', label: 'No', key: '2'},
                ]
              }}
              isRequired
            />
          )}
        </div>

        { buttons }
      </div>
    </MainView>
  );
};

BMIQuestions.propTypes = {
  buttons: PropTypes.element,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
};

